import React from "react";
import { Link } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Dashboard from "./Dashboard";
import ProtectedRoute from "../components/ProtectedRoute";

const EMBLEM_URL = "https://customer-assets-agu9un31.emergentagent.net/job_player-analytics-73/artifacts/4wc3ngyr_MBDG-Emblem-OnDark.png.webp";

// Public welcome page at "/". Logged-in users go straight to their dashboard (ProtectedRoute handles family redirects).
export default function LandingPage() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" /></div>;
  if (user) return <ProtectedRoute><Dashboard /></ProtectedRoute>;
  return (
    <main className="relative min-h-screen bg-[#0a0a0a] text-white overflow-hidden" data-testid="landing-page">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(230,185,70,0.14),transparent_55%)]" />
      <div className="pointer-events-none absolute -right-[10%] top-[18%] font-heading text-[26vw] leading-none uppercase text-white/[0.025] select-none">Maximize</div>
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
        <div className="flex items-center gap-3">
          <img src={EMBLEM_URL} alt="Maximize Analytics" className="h-12 w-auto" data-testid="landing-logo" />
          <span className="font-heading uppercase text-2xl tracking-wide" data-testid="landing-app-name">Maximize Analytics</span>
        </div>
        <Link to="/login" className="flex items-center gap-2 px-5 py-2.5 bg-[var(--org-accent)] text-[#121212] text-[11px] uppercase tracking-[0.3em] font-bold hover:brightness-110 transition" data-testid="landing-login-btn">
          <LogIn className="w-4 h-4" /> Sign in
        </Link>
      </header>
      <section className="relative z-10 px-6 lg:px-12 pt-[12vh] lg:pt-[16vh] max-w-[1100px]">
        <div className="text-[11px] uppercase tracking-[0.45em] text-[var(--org-accent)] font-bold animate-[fadeUp_.6s_ease-out_both]">Maximize Analytics · by Maximize Basketball</div>
        <h1 className="font-heading uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.9] mt-4 animate-[fadeUp_.6s_ease-out_.1s_both]">
          Player Tracker &amp;<br />Development<br /><span className="text-[var(--org-accent)]">Database</span>
        </h1>
        <p className="text-base md:text-lg text-[#a0a0a0] mt-6 max-w-[620px] animate-[fadeUp_.6s_ease-out_.2s_both]" data-testid="landing-tagline">
          Maximize Analytics is a player tracker and development database for basketball players: live stats and trends, film breakdowns, development inventory tracking, and Google Calendar integration for scheduling training sessions — built for coaches, players and families.
        </p>
        <ul className="mt-6 flex flex-wrap gap-2 animate-[fadeUp_.6s_ease-out_.25s_both]" data-testid="landing-features">
          {["Stats & trends", "Film breakdowns", "Development tracking", "Google Calendar scheduling"].map(f => (
            <li key={f} className="px-3 py-1.5 border border-white/15 text-[10px] uppercase tracking-[0.25em] text-[#c0c0c0]">{f}</li>
          ))}
        </ul>
        <Link to="/login" className="inline-flex items-center gap-2 mt-10 px-6 py-3 border border-white/20 text-[11px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] transition animate-[fadeUp_.6s_ease-out_.3s_both]" data-testid="landing-login-cta">
          Sign in <LogIn className="w-4 h-4" />
        </Link>
      </section>
      <footer className="relative z-10 px-6 lg:px-12 py-8 mt-[14vh] flex flex-wrap gap-6 text-[10px] uppercase tracking-[0.3em] text-[#575757]">
        <span>© {new Date().getFullYear()} Maximize Analytics · Maximize Basketball</span>
        <Link to="/privacy" className="hover:text-white">Privacy</Link>
        <Link to="/terms" className="hover:text-white">Terms</Link>
      </footer>
    </main>
  );
}
